// hooks/useVideoSchema.ts
import { useEffect } from 'react';

export interface VideoSchemaData {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl: string;
  embedUrl: string;
  author?: {
    name: string;
    url?: string;
  };
  publisher?: {
    name: string;
    logo?: string;
  };
  interactionStatistic?: {
    interactionType: string;
    userInteractionCount: number;
  }[];
}

export const useVideoSchema = (videoData: VideoSchemaData | null, pageUrl: string) => {
  useEffect(() => {
    // Remove any existing video schema
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"][data-video-schema]');
    existingSchemas.forEach(schema => schema.remove());

    if (!videoData) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": videoData.name,
      "description": videoData.description,
      "thumbnailUrl": videoData.thumbnailUrl,
      "uploadDate": videoData.uploadDate,
      "duration": videoData.duration,
      "contentUrl": videoData.contentUrl,
      "embedUrl": videoData.embedUrl,
      "url": pageUrl,
      "publisher": {
        "@type": "Organization",
        "name": videoData.publisher?.name || "StrymHub",
        "logo": {
          "@type": "ImageObject",
          "url": videoData.publisher?.logo || `${import.meta.env.VITE_NODE_ENV === 'production' ? 'https://strymhub.com' : 'http://localhost:5173'}/android-chrome-512x512.png`
        }
      },
      "author": videoData.author ? {
        "@type": "Person",
        "name": videoData.author.name,
        "url": videoData.author.url
      } : undefined,
      "interactionStatistic": videoData.interactionStatistic?.map(stat => ({
        "@type": "InteractionCounter",
        "interactionType": {
          "@type": "WatchAction"
        },
        "userInteractionCount": stat.userInteractionCount
      })) || [],
      "isLiveBroadcast": true,
      "publication": {
        "@type": "BroadcastEvent",
        "isLiveBroadcast": true,
        "startDate": videoData.uploadDate
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-video-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"][data-video-schema]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, [videoData, pageUrl]);
};